import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    IconButton,
    Grid,
    Box,
    Popover,
    Button,
    Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { getCurrencySymbol, toFixedTrunc } from '../../../client/Juno/helpers';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ActionConfirmModal from '../common/ActionConfirmModal';
import useDeleteStatements from './useDeleteStatements';
import clsx from 'clsx';

export default function StatementDialogComponent({
    open,
    onModalClose,
    statementsData = {},
    onDownload,
}) {
    const imageBaseUrl = useSelector(state => state.config.image_base_url);
    const activeCurrencyPage = localStorage.getItem('activeCurrencyPage');
    const [anchorEl, setAnchorEl] = useState(null);
    const handlePopoverOpen = event => setAnchorEl(event.currentTarget);
    const handlePopoverClose = () => setAnchorEl(null);
    const openPopover = Boolean(anchorEl);
    const popoverId = openPopover ? 'download-popover' : undefined;
    const [selectedStatment, setSelectedStatement, deleteStatement] = useDeleteStatements();

    const clientData = useSelector(state => state.juno.clientData);
    const readOnly = clientData && clientData.readOnly ? clientData.readOnly : false;

    return (
        <>
            <Dialog
                open={open}
                onClose={e => {
                    onModalClose(e);
                    handlePopoverClose();
                }}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    className: 'rounded-xl  py-1  dark:bg-[#26272B] dark:text-white !border-0',
                }}>
                <DialogTitle className="flex items-center justify-between font-medium text-xl py-2 dark:!text-white !border-0">
                    Statement Details
                    <IconButton aria-label="close" onClick={onModalClose} sx={{ ml: 2 }}>
                        <CloseIcon className="dark:text-white" />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ pt: 1, px: 3 }}>
                    <Grid container direction="column">
                        <Box sx={{ py: 1, mb: 2 }}>
                            <Typography className="text-[#51525C] !font-inter font-medium text-[14px] leading-[20px] dark:!text-text">
                                DATE:
                            </Typography>
                            <Typography className="text-[#18181B] !font-inter font-medium  dark:!font-semibold text-[14px] leading-[20px] dark:!text-text">
                                {format(new Date(statementsData.createdAt), 'dd.MM.yy HH:mm')}
                            </Typography>
                        </Box>
                        <Box sx={{ borderBottom: '1px solid #E4E4E7', pb: 1, mb: 2 }}>
                            <Typography className="text-[#51525C] !font-inter font-medium text-[14px] leading-[20px] dark:!text-text">
                                CURRENCY:
                            </Typography>
                            <div className="flex gap-2 items-center">
                                {statementsData.matchedCurrency?.currencyIcon && (
                                    <div>
                                        <img
                                            src={`${imageBaseUrl}${statementsData.matchedCurrency?.currencyIcon}`}
                                            alt={statementsData.matchedCurrency.currencyShortName}
                                            className="w-5 h-5"
                                        />
                                    </div>
                                )}
                                <Typography className="!text-text !font-inter font-medium  dark:!font-semibold text-[14px] leading-[20px] dark:!text-text">
                                    {statementsData.currency}
                                </Typography>
                            </div>
                        </Box>
                        <Box sx={{ borderBottom: '1px solid #E4E4E7', pb: 1, mb: 2 }}>
                            <Typography className="text-[#51525C] !font-inter font-medium text-[14px] leading-[20px] dark:!text-text">
                                OPENING BALANCE:
                            </Typography>
                            <Typography className="!text-text !font-inter font-medium  dark:!font-semibold text-[14px] leading-[20px] dark:!text-text">
                                {getCurrencySymbol(statementsData.currency)}{' '}
                                {toFixedTrunc(
                                    statementsData.openingBalance,
                                    activeCurrencyPage === 'crypto' ? 6 : 2,
                                )}
                            </Typography>
                        </Box>

                        <Box sx={{ borderBottom: '1px solid #E4E4E7', pb: 1, mb: 2 }}>
                            <Typography className="text-[#51525C] !font-inter font-medium text-[14px] leading-[20px] dark:!text-text">
                                CLOSING BALANCE:
                            </Typography>
                            <Typography className="!text-text !font-inter font-medium  dark:!font-semibold text-[14px] leading-[20px] dark:!text-text">
                                {getCurrencySymbol(statementsData.currency)}{' '}
                                {toFixedTrunc(
                                    statementsData.closingBalance,
                                    activeCurrencyPage === 'crypto' ? 6 : 2,
                                )}
                            </Typography>
                        </Box>
                    </Grid>
                    <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
                        <IconButton
                            onClick={() => {
                                readOnly ? null : setSelectedStatement(statementsData);
                            }}>
                            <div className={clsx("w-10 h-10 !bg-secondary rounded-lg flex items-center justify-center", readOnly && "opacity-50")}>
                                <DeleteOutlinedIcon className="!text-text w-4 h-4" />
                            </div>
                        </IconButton>
                        <div className="flex">
                            <IconButton onClick={() => onDownload(statementsData, 'pdf')}>
                                <div className="w-10 h-10 !bg-secondary rounded-lg flex items-center justify-center">
                                    <RemoveRedEyeOutlinedIcon className="!text-text w-4 h-4" />
                                </div>
                            </IconButton>
                            <IconButton onClick={() => onDownload(statementsData, 'excel')}>
                                <div className="w-10 h-10 !bg-secondary rounded-lg flex items-center justify-center">
                                    <img
                                        alt="download file"
                                        src={`${imageBaseUrl}/downloadFileIcon.svg`}
                                        className="w-5 h-5 !text-text  dark:filter dark:invert dark:brightness-1 dark:contrast-200 transition-all"
                                    />
                                </div>
                            </IconButton>
                        </div>
                        <Popover
                            id={popoverId}
                            open={openPopover}
                            anchorEl={anchorEl}
                            onClose={handlePopoverClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                            <Box p={2} display="flex" flexDirection="column" gap={1}>
                                <Button
                                    startIcon={
                                        <img
                                            src={`${imageBaseUrl}/adminUi/pdf.svg`}
                                            alt="pdf"
                                            width={20}
                                        />
                                    }
                                    onClick={() => {
                                        handlePopoverClose();
                                        onDownload(statementsData, 'pdf');
                                    }}
                                    sx={{
                                        justifyContent: 'flex-start',
                                        textTransform: 'none',
                                        color: '#000',
                                    }}>
                                    PDF file
                                </Button>
                                <Button
                                    startIcon={
                                        <img
                                            src={`${imageBaseUrl}/adminUi/excel.svg`}
                                            alt="excel"
                                            width={20}
                                        />
                                    }
                                    onClick={() => {
                                        handlePopoverClose();
                                        onDownload(statementsData, 'excel');
                                    }}
                                    sx={{
                                        justifyContent: 'flex-start',
                                        textTransform: 'none',
                                        color: '#000',
                                    }}>
                                    Excel file
                                </Button>
                            </Box>
                        </Popover>
                    </Box>
                </DialogContent>
            </Dialog>
            {selectedStatment ? (
                <ActionConfirmModal
                    open={Boolean(selectedStatment)}
                    type={'delete'}
                    onCancel={() => setSelectedStatement(null)}
                    heading="delete statement!"
                    subHeading={'are you sure you want to delete the statement?'}
                    onConfirm={() => deleteStatement(statementsData._id, onModalClose)}
                    body={() => (
                        <div className="text-center justify-center text-text text-base font-semibold font-['Inter'] uppercase leading-normal">
                            {statementsData.currency} statement
                        </div>
                    )}
                    actionButtonText="delete"
                />
            ) : null}
        </>
    );
}
